import { Avatar, Box, Button, Modal, Select } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconUser } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useUsersUpdate } from '../../api/hooks/Users/useUsersUpdate'
import GuardianEditModal from './GuardianEditModal'
import SaveButton from '../common/SaveButton'
import ModalInput from '../common/ModalInput'
import styles from '../../styles/manager/EditUserModal.module.scss'
import { Icon } from '../../assets/icons'
import axios from 'axios'

interface EditUserModalProps {
  opened: boolean
  onClose: () => void
  userData: any
}

interface UserFormData {
  NAME: string
  EMAIL: string
  BIRTH: string
  PHONE: string
  SCHOOL: string
  ADDR: string
  USER_ID: string
  PARENT_ID: number
  GUARDIAN_NAME: string
  GUARDIAN_EMAIL: string
  PASSWORD: string
  STATE: string
  COUNTRY: string
  CITY: string
  POSTCODE: string
  SUB: string
}

const EditUserModal = ({ opened, onClose, userData }: EditUserModalProps) => {
  const [formData, setFormData] = useState<UserFormData>({
    NAME: '',
    EMAIL: '',
    BIRTH: '',
    PHONE: '',
    SCHOOL: '',
    ADDR: '',
    USER_ID: '',
    PASSWORD: '',
    STATE: 'Y',
    PARENT_ID: 0,
    GUARDIAN_NAME: '',
    GUARDIAN_EMAIL: '',
    COUNTRY: '',
    CITY: '',
    POSTCODE: '',
    SUB: '',
  })
  const [guardianModalOpened, setGuardianModalOpened] = useState(false)
  const [guardianInfo, setGuardianInfo] = useState<{
    name: string
    email: string
  } | null>(null)

  const updateUserMutation = useUsersUpdate()
  //const [countryOptions, setCountryOptions] = useState<{ value: string; label: string }[]>([])

  const countryOptions = [
    { value: 'KR', label: 'South Korea' },
    { value: 'US', label: 'United States' },
    { value: 'JP', label: 'Japan' },
    { value: 'CN', label: 'China' },
    { value: 'VN', label: 'Vietnam' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'FR', label: 'France' },
    { value: 'DE', label: 'Germany' },
    { value: 'CA', label: 'Canada' },
    { value: 'AU', label: 'Australia' },
    { value: 'IN', label: 'India' },
    { value: 'TH', label: 'Thailand' },
    { value: 'ID', label: 'Indonesia' },
    { value: 'PH', label: 'Philippines' },
    { value: 'RU', label: 'Russia' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'MY', label: 'Malaysia' },
    { value: 'SG', label: 'Singapore' },
    { value: 'BR', label: 'Brazil' },
    { value: 'MX', label: 'Mexico' },
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'TR', label: 'Turkey' },
    { value: 'AR', label: 'Argentina' },
    { value: 'ZA', label: 'South Africa' },
  ]

  useEffect(() => {
    if (opened && userData) {
      setFormData({
        NAME: userData.NAME || '',
        EMAIL: userData.EMAIL || '',
        BIRTH: userData.BIRTH || '',
        PHONE: userData.PHONE || '',
        SCHOOL: userData.SCHOOL || '',
        ADDR: userData.ADDR || '',
        USER_ID: userData.USER_ID || '',
        PARENT_ID: userData.PARENT_ID,
        GUARDIAN_NAME: userData.GUARDIAN_NAME || '',
        GUARDIAN_EMAIL: userData.GUARDIAN_EMAIL || '',
        PASSWORD: userData.PASSWORD || '',
        STATE: userData.STATE || 'Y',
        COUNTRY: userData.COUNTRY || '',
        CITY: userData.CITY || '',
        SUB: userData.SUB || '',
        POSTCODE: userData.POSTCODE || '',
      })

      if (userData.GUARDIAN_NAME || userData.GUARDIAN_EMAIL) {
        setGuardianInfo({
          name: userData.GUARDIAN_NAME,
          email: userData.GUARDIAN_EMAIL,
        })
      } else {
        setGuardianInfo(null)
      }
    }
  }, [opened, userData])

  useEffect(() => {
    if (updateUserMutation.isSuccess) {
      notifications.show({
        title: 'Success',
        message: 'User updated successfully!',
        color: 'green',
      })
      handleClose()
    } else if (updateUserMutation.isError) {
      const error = updateUserMutation.error as any
      const status = error?.response?.status || error?.status || null

      if (status === 409) {
        notifications.show({
          title: '중복 오류',
          message: '이미 존재하는 ID입니다.',
          color: 'red',
        })
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to update user. Please try again.',
          color: 'red',
        })
      }
    }
  }, [updateUserMutation.isSuccess, updateUserMutation.isError])

  const handleInputChange = (field: keyof UserFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSaveButton = () => {
    if (!formData.NAME || !formData.USER_ID) {
      notifications.show({
        title: 'Error',
        message: 'Name and ID are required.',
        color: 'red',
      })
      return
    }

    const passwordChanged = formData.PASSWORD !== (userData?.PASSWORD || '')
    if (passwordChanged && formData.PASSWORD && !isValidPassword(formData.PASSWORD)) {
      notifications.show({
        title: 'Invalid password',
        message: 'At least 8 characters with letters and numbers/special characters',
        color: 'red',
      })
      return
    }

    updateUserMutation.mutate({
      ...formData,
      ID: userData?.ID,
    })
  }

  const handleClose = () => {
    setFormData({
      NAME: '',
      EMAIL: '',
      BIRTH: '',
      PHONE: '',
      SCHOOL: '',
      ADDR: '',
      USER_ID: '',
      PASSWORD: '',
      STATE: 'Y',
      PARENT_ID: 0,
      GUARDIAN_NAME: '',
      GUARDIAN_EMAIL: '',
      COUNTRY: '',
      CITY: '',
      SUB: '',
      POSTCODE: '',
    })
    setGuardianInfo(null)
    onClose()
  }

  const isValidPassword = (pw: string) => {
    const lengthCheck = pw.length >= 8
    const letterCheck = /[a-zA-Z]/.test(pw)
    const numberOrSymbolCheck = /[\d\W]/.test(pw)
    return lengthCheck && letterCheck && numberOrSymbolCheck
  }

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        size="xl"
        padding="xl"
        styles={{
          content: {
            borderRadius: '15px',
          },
          close: {
            top: '-20px',
            right: '10px',
          },
        }}
      >
        <p className={styles.title}>Edit User</p>
        <div className={styles.container}>
          <div className={styles.topWrapper}>
            <div className={styles.userInfoWrapper}>
              <div className={styles.userInfoInnerWrapper}>
                <Avatar size={50} color="gray" className={styles.avatar}>
                  <IconUser size={40} />
                </Avatar>
                <div className={styles.userFormWrapper}>
                  <p className={styles.textLg}>User name</p>
                  <ModalInput
                    placeholder="Insert name"
                    value={formData.NAME}
                    onChange={(e) => handleInputChange('NAME', e.target.value)}
                    required
                  />
                  <p className={`${styles.textLg} ${styles.emailLabel}`}>
                    email address
                  </p>
                  <ModalInput
                    placeholder="Insert email address"
                    value={formData.EMAIL}
                    onChange={(e) => handleInputChange('EMAIL', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className={styles.levelInfoWrapper}>
              <div className={styles.levelHeaderWrapper}>
                <Icon name="info" size={20} />
                <p className={styles.textLg}>Level info</p>
              </div>
              <div className={styles.levelInputsWrapper}>
                <div className={styles.levelInputItem}>
                  <p className={`${styles.textSm} ${styles.inputLabel}`}>Level</p>
                  <p className={styles.textLg}>{userData?.LEVEL || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.generalInfoWrapper}>
            <div className={styles.generalInfoHeaderWrapper}>
              <Icon name="info" size={20} />
              <p className={styles.textLg}>General information</p>
            </div>

            <div className={styles.generalInfoFieldsWrapper}>
              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>ID</p>
                <ModalInput
                  value={formData.USER_ID}
                  onChange={(e) => handleInputChange('USER_ID', e.target.value)}
                  required
                />
              </div>

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Date of birth
                </p>
                <ModalInput
                  placeholder="year.mm.yy"
                  type="date"
                  value={formData.BIRTH}
                  onChange={(e) => handleInputChange('BIRTH', e.target.value)}
                />
              </div>

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Password
                </p>
                <ModalInput
                  type="password"
                  value={formData.PASSWORD}
                  onChange={(e) =>
                    handleInputChange('PASSWORD', e.target.value)
                  }
                  required
                />
              </div>

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  School
                </p>
                <ModalInput
                  value={formData.SCHOOL}
                  onChange={(e) => handleInputChange('SCHOOL', e.target.value)}
                />
              </div>

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>Phone</p>
                <ModalInput
                  value={formData.PHONE}
                  onChange={(e) => handleInputChange('PHONE', e.target.value)}
                />
              </div>

              <div className={styles.divider}></div>

              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Country
                </p>
                <Select
                  data={countryOptions}
                  value={formData.COUNTRY}
                  onChange={(value) =>
                    handleInputChange('COUNTRY', value || '')
                  }
                  placeholder="Select country"
                />
              </div>
              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>City</p>
                <ModalInput
                  value={formData.CITY}
                  onChange={(e) => handleInputChange('CITY', e.target.value)}
                  placeholder="City name"
                />
              </div>
              <div className={styles.fieldItem}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Postcode
                </p>
                <ModalInput
                  value={formData.POSTCODE}
                  onChange={(e) =>
                    handleInputChange('POSTCODE', e.target.value)
                  }
                  placeholder="Zip or postal code"
                  required
                />
              </div>
              <div className={styles.fullWidthField}>
                <p className={`${styles.textSm} ${styles.inputLabel}`}>
                  Address
                </p>
                <ModalInput
                  value={formData.ADDR}
                  onChange={(e) => handleInputChange('ADDR', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className={styles.bottomWrapper}>
            <div className={styles.guardianWrapper}>
              <div className={styles.guardianHeaderWrapper}>
                <Icon name="info" size={20} />
                <p className={styles.textLg}>Guardian info</p>
              </div>

              {guardianInfo ? (
                <Box
                  className={styles.guardianInfoBox}
                  onClick={() => setGuardianModalOpened(true)}
                >
                  <div className={styles.guardianInfoContent}>
                    <Avatar size={40} color="blue">
                      <IconUser size={20} />
                    </Avatar>
                    <div>
                      <p className={styles.textSm}>{guardianInfo.name}</p>
                      <p className={`${styles.textXs} ${styles.textDimmed}`}>
                        {guardianInfo.email}
                      </p>
                    </div>
                  </div>
                </Box>
              ) : (
                <Box
                  className={styles.noGuardianBox}
                  onClick={() => setGuardianModalOpened(true)}
                >
                  <p className={`${styles.textSm} ${styles.textDimmed}`}>
                    No Guardian Information
                  </p>
                </Box>
              )}
            </div>

            <div className={styles.statusWrapper}>
              <div className={styles.statusHeaderWrapper}>
                <Icon name="info" size={20} />
                <p className={styles.textLg}>Status</p>
              </div>
              <div className={styles.statusButtonsWrapper}>
                <Button
                  variant={formData.STATE === 'Y' ? 'filled' : 'outline'}
                  color={formData.STATE === 'Y' ? 'gray' : 'gray'}
                  onClick={() => handleInputChange('STATE', 'Y')}
                  className={
                    formData.STATE === 'Y'
                      ? styles.activeButton
                      : styles.activeButtonOutline
                  }
                >
                  Active
                </Button>
                <Button
                  variant={formData.STATE === 'N' ? 'filled' : 'outline'}
                  color="gray"
                  onClick={() => handleInputChange('STATE', 'N')}
                  className={
                    formData.STATE === 'N'
                      ? styles.inactiveButton
                      : styles.inactiveButtonOutline
                  }
                >
                  Inactive
                </Button>
              </div>
            </div>
          </div>

          <div className={styles.buttonWrapper}>
            <SaveButton onClick={handleClose}>Cancel</SaveButton>
            <SaveButton onClick={handleSaveButton}>Update</SaveButton>
          </div>
        </div>
      </Modal>

      <GuardianEditModal
        opened={guardianModalOpened}
        onClose={() => setGuardianModalOpened(false)}
        parentId={userData?.PARENT_ID}
      />
    </>
  )
}

export default EditUserModal
